import { useNavigate } from 'react-router-dom';
import { useProfileContext } from '@/contexts/profile-context';
import { ProfileCompletionDialog } from './profile-completion-dialog';
import { ProfileStrengthData } from '@/types/profile';
import { useAuth } from '@/components/auth/AuthProvider';

export function GlobalProfileCompletionDialog() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    profile, 
    shouldShowCompletionDialog, 
    dismissCompletionDialog,
    isLoading
  } = useProfileContext();

  // Early return if no user or still loading
  if (!user || isLoading) {
    return null;
  }

  // Early return if no profile data available
  if (!profile) {
    return null;
  }

  // Create strength data from profile
  const strengthData = (() => {
    if (profile) {
      // Use profile strength directly from profile context (already calculated by backend)
      const profileStrengthData: ProfileStrengthData = {
        totalStrength: profile.profile_strength || 0,
        categories: {}, // We don't need category breakdown for the dialog
        completedFields: [], // We don't need field-level details for the dialog
        pendingFields: []
      };
      
      return profileStrengthData;
    }
    
    // Since profiles are created during signup, this shouldn't happen
    // If no profile exists, there might be a data loading issue
    return null;
  })();

  const handleCompleteProfile = () => {
    dismissCompletionDialog();
    navigate('/profile');
  };

  const handleNavigateToField = (fieldKey: string, categoryKey: string) => {
    dismissCompletionDialog();
    navigate('/profile', { 
      state: { 
        editMode: true, 
        initialTab: categoryKey, 
        highlightField: fieldKey 
      } 
    });
  };

  const handleClose = () => {
    dismissCompletionDialog();
  };

  // Don't render if we don't have the necessary data
  if (!strengthData || !profile || !user) {
    return null;
  }

  // Additional check: only show if dialog should actually be shown
  if (!shouldShowCompletionDialog) {
    return null;
  }

  return (
    <ProfileCompletionDialog
      isOpen={shouldShowCompletionDialog}
      onClose={handleClose}
      onCompleteProfile={handleCompleteProfile}
      onNavigateToField={handleNavigateToField}
      strengthData={strengthData}
      userType={profile.profile_type}
    />
  );
}
